import React, { useState, useEffect, useCallback } from 'react';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';

const ALL_FEATURES = [
  { key: 'dashboard', label: 'Dashboard' },
  { key: 'whatsapp',  label: 'WhatsApp & Bot' },
  { key: 'chats',     label: 'Live Chats' },
  { key: 'leads',     label: 'Leads CRM' },
];

function LicenseMeter({ label, used, total }) {
  const pct = total > 0 ? Math.min(100, Math.round((used / total) * 100)) : 0;
  const color = pct >= 100 ? '#ef4444' : pct >= 80 ? '#f97316' : '#22c55e';
  return (
    <div style={{ flex: 1, minWidth: 200 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
        <span style={{ color: '#94a3b8', fontSize: 13, fontWeight: 600 }}>{label}</span>
        <span style={{ color, fontWeight: 700, fontSize: 13 }}>{used} / {total}</span>
      </div>
      <div style={{ background: '#0f172a', borderRadius: 6, height: 8, overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${pct}%`, background: color, borderRadius: 6, transition: 'width 0.3s' }} />
      </div>
      {pct >= 100 && <p style={{ color: '#ef4444', fontSize: 12, marginTop: 4 }}>Limit reached</p>}
    </div>
  );
}

function FeatureCheckboxes({ selected, onChange, availableFeatures }) {
  const available = availableFeatures || ALL_FEATURES.map(f => f.key);

  function toggle(key) {
    if (['dashboard', 'whatsapp', 'chats'].includes(key)) return;
    if (selected.includes(key)) onChange(selected.filter(f => f !== key));
    else onChange([...selected, key]);
  }

  return (
    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 4 }}>
      {ALL_FEATURES.filter(f => available.includes(f.key)).map(f => {
        const locked = ['dashboard', 'whatsapp', 'chats'].includes(f.key);
        const checked = selected.includes(f.key);
        return (
          <label key={f.key} style={{
            display: 'flex', alignItems: 'center', fontSize: 12, cursor: locked ? 'default' : 'pointer',
            background: checked ? '#1e3a5f' : '#0f172a', color: checked ? '#60a5fa' : '#64748b',
            padding: '4px 10px', borderRadius: 6, border: `1px solid ${checked ? '#3b82f6' : '#334155'}`,
            opacity: locked ? 0.7 : 1,
          }}>
            <input type="checkbox" checked={checked} onChange={() => toggle(f.key)} style={{ marginRight: 5 }} disabled={locked} />
            {f.label}{locked ? ' (default)' : ''}
          </label>
        );
      })}
    </div>
  );
}

export default function Users() {
  const { user } = useAuth();
  const [orgs, setOrgs] = useState([]);
  const [selectedOrgId, setSelectedOrgId] = useState('');
  const [org, setOrg] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');

  const [form, setForm] = useState({ email: '', password: '', name: '', role: 'user', allowedApps: ['bot'], allowedFeatures: ['dashboard', 'whatsapp', 'chats'] });
  const [editingUserId, setEditingUserId] = useState(null);
  const [editFeatures, setEditFeatures] = useState([]);

  const isSuperAdmin = user?.role === 'superadmin';
  const orgId = isSuperAdmin ? selectedOrgId : user?.organizationId;
  const orgFeatures = org?.allowedFeatures || user?.orgFeatures || ALL_FEATURES.map(f => f.key);
  const orgApps = org?.allowedApps || user?.orgApps || ['bot'];

  useEffect(() => {
    if (isSuperAdmin) {
      api.get('/api/org/organizations').then(({ data }) => {
        setOrgs(data);
        if (data.length > 0) setSelectedOrgId(data[0]._id);
      }).catch(() => {});
    }
  }, [isSuperAdmin]);

  useEffect(() => {
    if (!isSuperAdmin) {
      api.get('/api/org/my-org').then(({ data }) => setOrg(data)).catch(() => {});
    }
  }, [isSuperAdmin]);

  const fetchUsers = useCallback(async () => {
    if (!orgId) return;
    try {
      setLoading(true);
      setError('');
      const { data } = await api.get('/api/org/users', { params: { organizationId: orgId } });
      setUsers(data);
      if (isSuperAdmin) {
        const found = orgs.find(o => o._id === orgId);
        if (found) setOrg(found);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to fetch users');
    } finally {
      setLoading(false);
    }
  }, [orgId, orgs, isSuperAdmin]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  useEffect(() => {
    if (orgFeatures.length > 0) {
      setForm(f => ({ ...f, allowedFeatures: ['dashboard', 'whatsapp', 'chats'].filter(k => orgFeatures.includes(k)) }));
    }
  }, [orgFeatures.join(',')]); // eslint-disable-line

  async function handleCreate(e) {
    e.preventDefault();
    try {
      await api.post('/api/org/users', { ...form, organizationId: orgId });
      setForm({ email: '', password: '', name: '', role: 'user', allowedApps: ['bot'], allowedFeatures: ['dashboard', 'whatsapp', 'chats'] });
      flash('User created! WhatsApp client is starting.');
      fetchUsers();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to create user');
    }
  }

  async function toggleActive(u) {
    try {
      await api.patch(`/api/org/users/${u._id}`, { isActive: !u.isActive });
      setUsers(prev => prev.map(x => x._id === u._id ? { ...x, isActive: !u.isActive } : x));
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to update user');
    }
  }

  async function saveFeatures(userId) {
    try {
      const { data } = await api.patch(`/api/org/users/${userId}`, { allowedFeatures: editFeatures });
      setUsers(prev => prev.map(x => x._id === userId ? data : x));
      setEditingUserId(null);
      flash('Access updated!');
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to update features');
    }
  }

  const flash = msg => { setSuccessMsg(msg); setTimeout(() => setSuccessMsg(''), 4000); };

  if (user?.role !== 'superadmin' && user?.role !== 'orgadmin') {
    return <div style={{ padding: 40, color: '#f1f5f9' }}>Access Denied. You must be an Org Admin.</div>;
  }

  const licenseUsed = users.length;
  const licenseTotal = org?.licenseCount ?? '?';
  const licensePercent = licenseTotal !== '?' ? Math.min(100, Math.round((licenseUsed / licenseTotal) * 100)) : 0;

  const botLicenseTotal = org?.botLicenseCount ?? org?.licenseCount ?? 0;
  const bcLicenseTotal = org?.broadcasterLicenseCount ?? 0;
  const botUsers = users.filter(u => !u.allowedApps || u.allowedApps.includes('bot'));
  const bcUsers = users.filter(u => u.allowedApps?.includes('broadcaster'));
  const botAtLimit = botUsers.length >= botLicenseTotal;
  const bcAtLimit = bcUsers.length >= bcLicenseTotal;

  return (
    <div style={s.container}>
      <h2 style={s.title}>Users & Licenses</h2>
      <p style={s.desc}>Manage sub-users within your organization license quota. Control their access features.</p>

      {successMsg && <p style={{ color: '#22c55e', marginBottom: 16, fontWeight: 600 }}>{successMsg}</p>}
      {error && <p style={{ color: '#ef4444', marginBottom: 16 }}>{error}</p>}

      {isSuperAdmin && (
        <div style={s.card}>
          <h3 style={s.cardTitle}>Select Organization</h3>
          <select style={s.input} value={selectedOrgId} onChange={e => setSelectedOrgId(e.target.value)}>
            {orgs.map(o => <option key={o._id} value={o._id}>{o.name}</option>)}
          </select>
        </div>
      )}

      {orgId && (
        <div style={s.card}>
          <h3 style={s.cardTitle}>License Usage</h3>
          <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap' }}>
            {orgApps.includes('bot') && (
              <LicenseMeter label="🤖 Bot" used={botUsers.length} total={botLicenseTotal} />
            )}
            {orgApps.includes('broadcaster') && (
              <LicenseMeter label="📢 Broadcaster" used={bcUsers.length} total={bcLicenseTotal} />
            )}
          </div>
        </div>
      )}

      {orgId && !(botAtLimit && bcAtLimit) && (
        <div style={s.card}>
          <h3 style={s.cardTitle}>Create New Sub-User</h3>
          <form onSubmit={handleCreate}>
            <div style={s.formRow}>
              <input type="text" placeholder="Full name" style={s.input} required value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} />
              <input type="email" placeholder="Email" style={s.input} required value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} />
              <input type="password" placeholder="Password" style={s.input} required value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} />
              <select style={s.input} value={form.role} onChange={e => setForm({ ...form, role: e.target.value })}>
                <option value="user">User</option>
                <option value="orgadmin">Org Admin</option>
              </select>
            </div>
            {form.role === 'user' && (
              <>
                <div style={{ marginBottom: 14 }}>
                  <label style={s.featureLabel}>Assign Apps</label>
                  <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 6 }}>
                    {orgApps.map(appKey => {
                      const checked = form.allowedApps.includes(appKey);
                      const atLimit = appKey === 'bot' ? botAtLimit : bcAtLimit;
                      return (
                        <label key={appKey} style={{
                          display: 'flex', alignItems: 'center', gap: 6, cursor: atLimit && !checked ? 'not-allowed' : 'pointer',
                          background: checked ? '#1a2e1a' : '#0f172a',
                          border: `1px solid ${checked ? '#22c55e' : '#334155'}`,
                          borderRadius: 8, padding: '7px 14px', opacity: atLimit && !checked ? 0.5 : 1,
                        }}>
                          <input type="checkbox" checked={checked} disabled={atLimit && !checked}
                            onChange={() => {
                              if (checked) setForm({ ...form, allowedApps: form.allowedApps.filter(a => a !== appKey) });
                              else setForm({ ...form, allowedApps: [...form.allowedApps, appKey] });
                            }} />
                          <span style={{ color: checked ? '#86efac' : '#94a3b8', fontWeight: 600, fontSize: 13 }}>
                            {appKey === 'bot' ? '🤖 Bot Admin' : '📢 Broadcaster'}
                          </span>
                          {atLimit && !checked && <span style={{ fontSize: 10, color: '#ef4444' }}>Limit reached</span>}
                        </label>
                      );
                    })}
                  </div>
                </div>
                {form.allowedApps.includes('bot') && (
                  <div style={{ marginBottom: 16 }}>
                    <label style={s.featureLabel}>Bot Features</label>
                    <p style={{ color: '#64748b', fontSize: 12, margin: '4px 0 8px' }}>
                      Dashboard, WhatsApp & Chats are always included.
                    </p>
                    <FeatureCheckboxes selected={form.allowedFeatures} onChange={f => setForm({ ...form, allowedFeatures: f })} availableFeatures={orgFeatures} />
                  </div>
                )}
              </>
            )}
            <button type="submit" style={s.btn}>Create User</button>
          </form>
          <p style={{ color: '#64748b', fontSize: 12, marginTop: 12 }}>
            Each bot user gets their own WhatsApp number to scan independently.
          </p>
        </div>
      )}

      <div style={s.card}>
        <h3 style={s.cardTitle}>Current Users ({users.length})</h3>
        {loading ? (
          <p style={{ color: '#64748b' }}>Loading...</p>
        ) : users.length === 0 ? (
          <p style={{ color: '#64748b' }}>No users yet.</p>
        ) : (
          <table style={s.table}>
            <thead>
              <tr>{['Name', 'Email', 'Role', 'Apps', 'Bot Features', 'Status', 'Actions'].map(h => (
                <th key={h} style={s.th}>{h}</th>
              ))}</tr>
            </thead>
            <tbody>
              {users.map(u => (
                <React.Fragment key={u._id}>
                  <tr>
                    <td style={s.td}>{u.name}</td>
                    <td style={s.td}>{u.email}</td>
                    <td style={s.td}>
                      <span style={{ ...s.badge, background: u.role === 'orgadmin' ? '#1e3a5f' : '#1e293b', color: u.role === 'orgadmin' ? '#60a5fa' : '#94a3b8' }}>
                        {u.role}
                      </span>
                    </td>
                    <td style={s.td}>
                      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                        {(u.allowedApps || ['bot']).map(a => (
                          <span key={a} style={{ ...s.featurePill, background: a === 'broadcaster' ? '#2d1a4a' : '#1e3a5f', color: a === 'broadcaster' ? '#c084fc' : '#60a5fa' }}>
                            {a === 'bot' ? '🤖 Bot' : '📢 BC'}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td style={s.td}>
                      {u.role === 'orgadmin' ? (
                        <span style={{ color: '#64748b', fontSize: 12 }}>All org features</span>
                      ) : (
                        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                          {(u.allowedFeatures || []).filter(f => f !== 'dashboard' && f !== 'whatsapp' && f !== 'chats').map(f => (
                            <span key={f} style={s.featurePill}>{FEATURE_LABELS[f] || f}</span>
                          ))}
                          {(u.allowedFeatures || []).filter(f => f !== 'dashboard' && f !== 'whatsapp' && f !== 'chats').length === 0 && (
                            <span style={{ color: '#475569', fontSize: 12 }}>Default</span>
                          )}
                        </div>
                      )}
                    </td>
                    <td style={s.td}>
                      <span style={{ ...s.badge, background: u.isActive ? '#166534' : '#7f1d1d', color: u.isActive ? '#86efac' : '#fca5a5' }}>
                        {u.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td style={s.td}>
                      <div style={{ display: 'flex', gap: 6 }}>
                        {u._id !== user?.id && (
                          <button
                            style={{ ...s.btnSm, background: u.isActive ? '#7f1d1d' : '#166534', color: u.isActive ? '#fca5a5' : '#86efac' }}
                            onClick={() => toggleActive(u)}
                          >
                            {u.isActive ? 'Deactivate' : 'Activate'}
                          </button>
                        )}
                        {u.role === 'user' && (
                          <button
                            style={{ ...s.btnSm, background: '#1e3a5f', color: '#60a5fa' }}
                            onClick={() => { setEditingUserId(u._id); setEditFeatures(u.allowedFeatures || []); }}
                          >
                            Edit Access
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                  {editingUserId === u._id && (
                    <tr>
                      <td colSpan={6} style={{ padding: '0 0 12px', background: '#0d1829' }}>
                        <div style={{ padding: '16px 20px', borderTop: '1px solid #1e3a5f' }}>
                          <p style={{ color: '#94a3b8', fontSize: 13, marginBottom: 10 }}>
                            Editing access for <strong style={{ color: '#f1f5f9' }}>{u.name}</strong>:
                          </p>
                          <FeatureCheckboxes selected={editFeatures} onChange={setEditFeatures} availableFeatures={orgFeatures} />
                          <div style={{ display: 'flex', gap: 10, marginTop: 14 }}>
                            <button style={s.btn} onClick={() => saveFeatures(u._id)}>Save Access</button>
                            <button style={{ ...s.btnSm, background: '#334155', color: '#94a3b8' }} onClick={() => setEditingUserId(null)}>Cancel</button>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

const FEATURE_LABELS = { dashboard: 'Dashboard', whatsapp: 'WhatsApp', chats: 'Chats', leads: 'Leads' };

const s = {
  container: { padding: 40, flex: 1, background: '#0f172a', color: '#f1f5f9', overflowY: 'auto' },
  title: { fontSize: 28, marginBottom: 8 },
  desc: { color: '#94a3b8', marginBottom: 32, fontSize: 14 },
  card: { background: '#1e293b', padding: 24, borderRadius: 12, border: '1px solid #334155', marginBottom: 24 },
  cardTitle: { marginBottom: 16, color: '#e2e8f0', fontSize: 16, fontWeight: 600 },
  formRow: { display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginBottom: 12 },
  featureLabel: { fontSize: 13, fontWeight: 600, color: '#94a3b8', display: 'block' },
  input: { padding: '8px 12px', borderRadius: 6, border: '1px solid #334155', background: '#0f172a', color: '#fff', fontSize: 14, outline: 'none' },
  btn: { padding: '8px 20px', borderRadius: 6, border: 'none', background: '#3b82f6', color: '#fff', cursor: 'pointer', fontWeight: 600, fontSize: 14 },
  btnSm: { padding: '5px 12px', borderRadius: 6, border: 'none', cursor: 'pointer', fontWeight: 600, fontSize: 12 },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: { textAlign: 'left', padding: '10px 12px', color: '#64748b', fontSize: 13, borderBottom: '1px solid #334155' },
  td: { padding: '14px 12px', borderBottom: '1px solid #1e293b', color: '#cbd5e1', fontSize: 14 },
  badge: { padding: '3px 10px', borderRadius: 10, fontSize: 12, fontWeight: 600 },
  featurePill: { background: '#1e3a5f', color: '#60a5fa', padding: '2px 7px', borderRadius: 5, fontSize: 11, fontWeight: 600 },
};
